import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { useUpdateReportMarket } from "../../hooks/useUpdateReportMarket";
import { usePreflightMarkets } from "../../hooks/usePreflightMarkets";
import { serializeMarketFilter } from "../../lib/market-filter";
import { preventDialogDismissForDropdown } from "../../lib/prevent-dialog-dismiss-for-dropdown";
import type { PreflightReportSummary } from "../../types/preflight.types";
import { MarketSelect } from "../MarketSelect/MarketSelect";

export interface ChangeReportMarketDialogProps {
  report: Pick<PreflightReportSummary, "id" | "markets">;
  /** Custom trigger; defaults to a ghost "Change market" button. */
  trigger?: React.ReactNode;
  /** Controlled open state when the parent owns the trigger (e.g. header chip). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Permission-gated dialog to reassign a report's markets from the tenant list
 * (same multi-select as create). Optimistically patches detail + list caches and
 * adjusts market report counts; rolls back on error.
 */
export function ChangeReportMarketDialog({
  report,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: ChangeReportMarketDialogProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const currentIds = report.markets.map((market) => market.id);
  const [marketIds, setMarketIds] = useState<string[]>(currentIds);
  const { markets, isPending: areMarketsPending } = usePreflightMarkets();
  const mutation = useUpdateReportMarket(report);

  useEffect(() => {
    if (open) {
      setMarketIds(report.markets.map((market) => market.id));
    }
  }, [open, report.markets]);

  if (!permissions.PREFLIGHT_RENAME_REPORT.isOn) return null;

  const selected = marketIds.flatMap((id) => {
    const market = markets.find((row) => row.id === id);
    return market
      ? [{ id: market.id, kind: market.kind, name: market.name, code: market.code }]
      : [];
  });
  const canSubmit =
    selected.length > 0 &&
    selected.length === marketIds.length &&
    serializeMarketFilter(marketIds) !== serializeMarketFilter(currentIds);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger !== null ? (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button type="button" variant="ghost" size="sm">
              {t("preflight.changeMarket.trigger", "Change market")}
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent
        className="top-16 max-w-sm translate-y-0 sm:top-20 sm:max-w-sm"
        onPointerDownOutside={preventDialogDismissForDropdown}
        onInteractOutside={preventDialogDismissForDropdown}
      >
        <DialogHeader>
          <DialogTitle>
            {t("preflight.changeMarket.title", "Change report market")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "preflight.changeMarket.description",
              "Pick one or more markets. Saving replaces this report's current markets with your selection.",
            )}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              mutation.mutate(selected, { onSuccess: () => setOpen(false) });
            }
          }}
          className="flex min-w-0 max-w-full flex-col gap-4"
        >
          <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-hidden">
            <Label>
              {t("preflight.changeMarket.label", "Markets")}
            </Label>
            <MarketSelect
              markets={markets}
              value={marketIds}
              onChange={setMarketIds}
              mode="multi"
              size="default"
              disabled={areMarketsPending || mutation.isPending}
              className="w-full min-w-0 max-w-full"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              {t("preflight.changeMarket.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending
                ? t("preflight.changeMarket.saving", "Saving…")
                : t("preflight.changeMarket.save", "Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
